import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Loader2, Send, Trash2, CheckCircle2, X, Clock, User as UserIcon, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function AdminContactInbox() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [active, setActive] = useState(null);

    useEffect(() => {
        if (!user) return;
        if (user.role !== "admin") {
            toast.error("Accès réservé à l'administrateur");
            navigate("/dashboard");
            return;
        }
        reload();
        // eslint-disable-next-line
    }, [user]);

    const reload = async () => {
        try {
            const { data } = await api.get("/admin/contact-messages");
            setMessages(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally { setLoading(false); }
    };

    const visible = messages.filter(m =>
        filter === "all" ? true : filter === "pending" ? !m.handled : m.handled
    );

    const handled = messages.filter(m => m.handled).length;
    const pending = messages.length - handled;

    if (loading) return <div className="min-h-screen bg-paper flex items-center justify-center"><Loader2 className="animate-spin text-orange" /></div>;

    return (
        <div className="min-h-screen bg-paper flex flex-col">
            <Navbar />
            <main className="flex-1 max-w-7xl mx-auto px-6 lg:px-10 py-8 w-full" data-testid="admin-inbox-page">
                <button onClick={() => navigate("/admin")} className="text-xs font-mono text-muted2 hover:text-ink mb-4 inline-flex items-center gap-1">
                    <ArrowLeft size={12} /> Console admin
                </button>

                <div className="label-eyebrow">Administration · Boîte de réception</div>
                <h1 className="h-display text-4xl sm:text-5xl font-semibold leading-tight mt-1.5">Messages reçus</h1>
                <p className="text-sm text-muted2 mt-2">Tous les messages envoyés via le formulaire de contact public. Répondez directement depuis cette page — l'utilisateur recevra un e-mail provenant de <strong>noreply@profawz.fr</strong>.</p>

                <div className="flex items-center gap-2 mt-6 text-sm">
                    <button onClick={() => setFilter("all")} className={`px-3 py-1.5 ${filter === "all" ? "bg-ink text-white" : "border border-rule hover:bg-surface"}`} data-testid="inbox-filter-all">
                        Tous ({messages.length})
                    </button>
                    <button onClick={() => setFilter("pending")} className={`px-3 py-1.5 ${filter === "pending" ? "bg-orange text-white" : "border border-rule hover:bg-surface"}`} data-testid="inbox-filter-pending">
                        À traiter ({pending})
                    </button>
                    <button onClick={() => setFilter("handled")} className={`px-3 py-1.5 ${filter === "handled" ? "bg-emerald-600 text-white" : "border border-rule hover:bg-surface"}`} data-testid="inbox-filter-handled">
                        Traités ({handled})
                    </button>
                </div>

                <div className="grid lg:grid-cols-[1fr_1.4fr] gap-3 mt-4 min-h-[500px]">
                    <aside className="panel overflow-hidden flex flex-col">
                        <div className="flex-1 overflow-y-auto">
                            {visible.length === 0 ? (
                                <div className="p-10 text-center text-xs text-muted2 font-mono">
                                    <Mail className="mx-auto mb-2 text-muted2" size={28} />
                                    Aucun message {filter === "pending" ? "à traiter" : filter === "handled" ? "traité" : ""}.
                                </div>
                            ) : visible.map(m => {
                                const isActive = active?.id === m.id;
                                const replies = m.replies?.length || 0;
                                return (
                                    <button
                                        key={m.id}
                                        onClick={() => setActive(m)}
                                        className={`w-full text-left px-4 py-3 border-b border-rule hover:bg-surface flex items-start gap-3 ${isActive ? "bg-surface border-l-4 border-l-orange" : ""}`}
                                        data-testid={`inbox-msg-${m.id}`}
                                    >
                                        <div className={`w-9 h-9 flex items-center justify-center shrink-0 ${m.handled ? "bg-emerald-50 text-emerald-700" : "bg-orange/15 text-orange"}`}>
                                            {m.handled ? <CheckCircle2 size={16} /> : <Mail size={16} />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-sm font-semibold truncate">{m.name}</span>
                                                {replies > 0 && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-emerald-100 text-emerald-700 shrink-0">{replies} rép.</span>}
                                            </div>
                                            <div className="text-[10px] font-mono text-muted2 mt-0.5 truncate">{m.email}{m.organisation ? ` · ${m.organisation}` : ""}</div>
                                            <div className="text-xs mt-1 truncate text-ink/70">{m.message}</div>
                                            <div className="text-[10px] text-muted2 mt-1">{new Date(m.ts).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </aside>

                    <section className="panel overflow-hidden flex flex-col">
                        {!active ? (
                            <div className="flex-1 flex items-center justify-center text-center px-6">
                                <div>
                                    <Mail size={48} className="mx-auto text-muted2 mb-3" strokeWidth={1.2} />
                                    <h3 className="h-display text-lg font-semibold">Sélectionnez un message</h3>
                                    <p className="text-xs text-muted2 mt-1.5">Cliquez sur un message à gauche pour le lire et y répondre.</p>
                                </div>
                            </div>
                        ) : <MessageDetail msg={active} onAfterReply={() => { reload(); }} onDeleted={() => { setActive(null); reload(); }} />}
                    </section>
                </div>
            </main>
            <Footer />
        </div>
    );
}

function MessageDetail({ msg, onAfterReply, onDeleted }) {
    const [body, setBody] = useState("");
    const [subject, setSubject] = useState(`Re : votre message à Profawz`);
    const [sending, setSending] = useState(false);
    const [showReply, setShowReply] = useState(false);

    const sendReply = async (e) => {
        e?.preventDefault?.();
        if (body.trim().length < 2) {
            toast.error("Écrivez votre réponse.");
            return;
        }
        setSending(true);
        try {
            await api.post(`/admin/contact-messages/${msg.id}/reply`, {
                body: body.trim(),
                subject: subject || undefined,
                mark_handled: true,
            });
            toast.success(`Réponse envoyée à ${msg.email}`);
            setBody("");
            setShowReply(false);
            onAfterReply();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur d'envoi");
        } finally { setSending(false); }
    };

    const toggleHandled = async () => {
        try {
            await api.post(`/admin/contact-messages/${msg.id}/mark-handled?handled=${!msg.handled}`);
            toast.success(msg.handled ? "Marqué à traiter" : "Marqué comme traité");
            onAfterReply();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    };

    const remove = async () => {
        if (!window.confirm(`Supprimer définitivement ce message de ${msg.email} ?`)) return;
        try {
            await api.delete(`/admin/contact-messages/${msg.id}`);
            toast.success("Message supprimé");
            onDeleted();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
    };

    return (
        <div className="flex flex-col h-full">
            <div className="px-5 py-4 border-b border-rule">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="h-display text-lg font-semibold">{msg.name}</h2>
                            {msg.handled && <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200">Traité</span>}
                        </div>
                        <div className="mt-1 text-xs font-mono text-muted2 flex flex-wrap gap-3">
                            <span><UserIcon size={10} className="inline mr-1" />{msg.email}</span>
                            {msg.organisation && <span><Building2 size={10} className="inline mr-1" />{msg.organisation}</span>}
                            <span><Clock size={10} className="inline mr-1" />{new Date(msg.ts).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={toggleHandled} className="p-2 hover:bg-emerald-50 text-emerald-700" title={msg.handled ? "Marquer à traiter" : "Marquer traité"} data-testid="inbox-toggle-handled">
                            <CheckCircle2 size={16} />
                        </button>
                        <button onClick={remove} className="p-2 hover:bg-destructive/10 text-destructive" title="Supprimer" data-testid="inbox-delete-msg">
                            <Trash2 size={16} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-surface">
                <div className="bg-paper p-4 border border-rule">
                    <div className="text-[10px] font-mono text-muted2 mb-2">Message d'origine</div>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed text-ink">{msg.message}</p>
                </div>
                {msg.replies?.map(r => (
                    <div key={r.id} className="bg-emerald-50/50 p-4 border-l-4 border-l-emerald-500">
                        <div className="text-[10px] font-mono text-emerald-700 mb-2 flex items-center gap-2">
                            <Send size={10} /> Votre réponse · {new Date(r.sent_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                        </div>
                        {r.subject && <div className="text-xs font-semibold text-ink/70 mb-1.5">Objet : {r.subject}</div>}
                        <p className="text-sm whitespace-pre-wrap leading-relaxed text-ink">{r.body}</p>
                    </div>
                ))}
            </div>

            {showReply ? (
                <form onSubmit={sendReply} className="p-4 border-t border-rule bg-paper" data-testid="inbox-reply-form">
                    <div className="flex items-center justify-between mb-3">
                        <div className="label-eyebrow">Répondre — sera envoyé à {msg.email}</div>
                        <button type="button" onClick={() => setShowReply(false)} className="text-muted2 hover:text-ink" aria-label="Fermer">
                            <X size={16} />
                        </button>
                    </div>
                    <input
                        type="text"
                        className="field-input mb-2 !text-sm"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Objet de l'email"
                        data-testid="inbox-reply-subject"
                    />
                    <textarea
                        rows={6}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder="Bonjour, merci pour votre message…"
                        className="field-input resize-none !py-2 !text-sm"
                        autoFocus
                        data-testid="inbox-reply-body"
                    />
                    <div className="flex items-center justify-end gap-2 mt-2">
                        <button type="button" onClick={() => setShowReply(false)} className="btn-outline !text-xs">Annuler</button>
                        <button type="submit" disabled={!body.trim() || sending} className="btn-primary !text-xs" data-testid="inbox-reply-send">
                            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                            Envoyer la réponse
                        </button>
                    </div>
                </form>
            ) : (
                <div className="p-4 border-t border-rule bg-paper">
                    <button onClick={() => setShowReply(true)} className="btn-primary w-full !text-sm" data-testid="inbox-open-reply">
                        <Send size={14} /> Répondre à {msg.name}
                    </button>
                </div>
            )}
        </div>
    );
}
